/* eslint-disable @aix/no-hardcoded-model-strings -- This file defines URL-friendly slug mappings, not model API calls */
// modelSlugs.ts
// Utilities for converting between model IDs and URL-friendly slugs

import {
  EByteDanceImageGenerationModelType,
  ECreatifyVideoGenerationModelType,
  EHeyGenVideoGenerationModelType,
  FluxImageGenerationModelType,
  GoogleImageGenerationModelType,
  GoogleVideoGenerationModelType,
  HappyHorseVideoGenerationModelType,
  HunyuanVideoGenerationModelType,
  KlingAIVideoGenerationModelType,
  MinimaxImageGenerationModelType,
  MinimaxVideoGenerationModelType,
  NvidiaImageGenerationModelType,
  OpenAIImageGenerationModelType,
  OpenAIVideoGenerationModelType,
  SeedanceVideoGenerationModelType,
  SkyReelsVideoGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "../image/enums"

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface ModelInfo {
  id: string // Enum value (model ID)
  slug: string // Primary URL-friendly slug
  aliases: string[] // Alternative slugs that also resolve to this model
  displayName: string // Human readable name
  provider: string // Provider name (Google, Kling, OpenAI, etc.)
  type: "video" | "image" // Model type for routing
  tier: "pro" | "standard" | "other" // Quality tier for sorting
  version: number // Parsed version for sorting (e.g., 2.6)
}

// ─────────────────────────────────────────────────────────────────────────────
// MODEL REGISTRY
// ─────────────────────────────────────────────────────────────────────────────

// Manually curated model info for precise slug generation
const MODEL_REGISTRY: ModelInfo[] = [
  {
    id: ECreatifyVideoGenerationModelType.BOREAL,
    slug: "creatify-boreal",
    aliases: ["boreal"],
    displayName: "Creatify Boreal",
    provider: "Creatify",
    type: "video",
    tier: "standard",
    version: 1,
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // VIDEO MODELS
  // ═══════════════════════════════════════════════════════════════════════════

  {
    id: EHeyGenVideoGenerationModelType.HEYGEN_VIDEO,
    slug: "heygen-video",
    aliases: [],
    displayName: "HeyGen Video",
    provider: "HeyGen via FAL",
    type: "video",
    tier: "standard",
    version: 1,
  },

  // Google Veo
  {
    id: GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_PREVIEW,
    slug: "gemini-omni-flash",
    aliases: ["omni-flash", "gemini-omni"],
    displayName: "Gemini Omni Flash",
    provider: "Google",
    type: "video",
    tier: "standard",
    version: 1,
  },
  {
    id: GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_1_1,
    slug: "gemini-omni-1-1-flash",
    aliases: ["omni-1-1-flash", "gemini-omni-flash-1-1"],
    displayName: "Gemini Omni Flash 1.1",
    provider: "Google",
    type: "video",
    tier: "standard",
    version: 1.1,
  },
  {
    id: GoogleVideoGenerationModelType.VEO_3_1_LITE,
    slug: "veo-3-1-lite",
    aliases: ["veo31-lite", "veo-lite"],
    displayName: "Veo 3.1 Lite",
    provider: "Google",
    type: "video",
    tier: "standard",
    version: 3.1,
  },
  {
    id: GoogleVideoGenerationModelType.VEO_3_1,
    slug: "veo-3-1",
    aliases: ["veo3-1", "veo31"],
    displayName: "Veo 3.1",
    provider: "Google",
    type: "video",
    tier: "pro",
    version: 3.1,
  },
  {
    id: GoogleVideoGenerationModelType.VEO_3,
    slug: "veo-3",
    aliases: ["veo3"],
    displayName: "Veo 3",
    provider: "Google",
    type: "video",
    tier: "pro",
    version: 3.0,
  },
  {
    id: GoogleVideoGenerationModelType.VEO_2,
    slug: "veo-2",
    aliases: ["veo2"],
    displayName: "Veo 2",
    provider: "Google",
    type: "video",
    tier: "standard",
    version: 2.0,
  },

  // Kling (Replicate)
  {
    id: KlingAIVideoGenerationModelType.KLING_2_1,
    slug: "kling-pro-2-6",
    aliases: ["kling-2-6-pro"],
    displayName: "Kling Pro 2.6",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 2.6,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_2_1,
    slug: "kling-standard-2-6",
    aliases: ["kling-2-6"],
    displayName: "Kling Standard 2.6",
    provider: "Kling",
    type: "video",
    tier: "standard",
    version: 2.6,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_2_1,
    slug: "kling-2-1",
    aliases: ["kling21"],
    displayName: "Kling 2.1",
    provider: "Kling",
    type: "video",
    tier: "standard",
    version: 2.1,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_2_5_TURBO_PRO,
    slug: "kling-2-5-turbo-pro",
    aliases: ["kling-2-5", "kling25", "kling-turbo-pro"],
    displayName: "Kling 2.5 Turbo Pro",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 2.5,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_PRO_1_6,
    slug: "kling-pro-1-6",
    aliases: ["kling-1-6-pro"],
    displayName: "Kling Pro 1.6",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 1.6,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_STANDARD_1_6,
    slug: "kling-standard-1-6",
    aliases: ["kling-1-6"],
    displayName: "Kling Standard 1.6",
    provider: "Kling",
    type: "video",
    tier: "standard",
    version: 1.6,
  },

  // Kling 1.0 (via FAL.ai)
  {
    id: KlingAIVideoGenerationModelType.KLING_1_0_PRO,
    slug: "kling-pro-1-0",
    aliases: ["kling-1-0-pro"],
    displayName: "Kling Pro 1.0",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 1.0,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_1_0_STANDARD,
    slug: "kling-standard-1-0",
    aliases: ["kling-1-0"],
    displayName: "Kling Standard 1.0",
    provider: "Kling",
    type: "video",
    tier: "standard",
    version: 1.0,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_2_6,
    slug: "kling-2-6",
    aliases: ["kling26"],
    displayName: "Kling 2.6",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 2.6,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_3_0,
    slug: "kling-3-0",
    aliases: ["kling30", "kling-3"],
    displayName: "Kling 3.0",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 3.0,
  },
  {
    id: KlingAIVideoGenerationModelType.KLING_4_0,
    slug: "kling-4-0",
    aliases: ["kling4", "kling-4"],
    displayName: "Kling 4.0",
    provider: "Kling",
    type: "video",
    tier: "standard",
    version: 4.0,
  },

  // OpenAI Sora
  {
    id: OpenAIVideoGenerationModelType.SORA_2_PRO,
    slug: "sora-2-pro",
    aliases: ["sora2-pro"],
    displayName: "Sora 2 Pro",
    provider: "OpenAI",
    type: "video",
    tier: "pro",
    version: 2.0,
  },
  {
    id: OpenAIVideoGenerationModelType.SORA_2,
    slug: "sora-2",
    aliases: ["sora2"],
    displayName: "Sora 2",
    provider: "OpenAI",
    type: "video",
    tier: "standard",
    version: 2.0,
  },

  // Minimax Video
  {
    id: MinimaxVideoGenerationModelType.VIDEO_01,
    slug: "minimax-video",
    aliases: ["minimax-video-01"],
    displayName: "MiniMax Video",
    provider: "MiniMax",
    type: "video",
    tier: "standard",
    version: 1.0,
  },
  {
    id: MinimaxVideoGenerationModelType.VIDEO_01_DIRECTOR,
    slug: "minimax-video-director",
    aliases: ["video-01-director", "minimax-director"],
    displayName: "Video-01 Director",
    provider: "MiniMax",
    type: "video",
    tier: "standard",
    version: 1.0,
  },
  {
    id: MinimaxVideoGenerationModelType.VIDEO_01_LIVE,
    slug: "minimax-video-live",
    aliases: ["video-01-live", "minimax-live"],
    displayName: "Video-01 Live",
    provider: "MiniMax",
    type: "video",
    tier: "standard",
    version: 1.0,
  },
  {
    id: MinimaxVideoGenerationModelType.HAILUO_02_FAST,
    slug: "hailuo-02-fast",
    aliases: ["minimax-hailuo-02-fast", "hailuo02-fast"],
    displayName: "Hailuo 02 Fast",
    provider: "MiniMax",
    type: "video",
    tier: "other",
    version: 2.0,
  },

  // Minimax Hailuo 2.3
  {
    id: MinimaxVideoGenerationModelType.HAILUO_2_3,
    slug: "hailuo-2-3",
    aliases: ["minimax-hailuo-2-3", "hailuo23"],
    displayName: "Hailuo 2.3",
    provider: "MiniMax",
    type: "video",
    tier: "pro",
    version: 2.3,
  },
  {
    id: MinimaxVideoGenerationModelType.HAILUO_2_3_FAST,
    slug: "hailuo-2-3-fast",
    aliases: ["minimax-hailuo-2-3-fast", "hailuo23-fast"],
    displayName: "Hailuo 2.3 Fast",
    provider: "MiniMax",
    type: "video",
    tier: "other",
    version: 2.3,
  },

  // Google Veo Fast variants
  {
    id: GoogleVideoGenerationModelType.VEO_3_FAST,
    slug: "veo-3-fast",
    aliases: ["veo3-fast"],
    displayName: "Veo 3 Fast",
    provider: "Google",
    type: "video",
    tier: "standard",
    version: 3.0,
  },
  {
    id: GoogleVideoGenerationModelType.VEO_3_1_FAST,
    slug: "veo-3-1-fast",
    aliases: ["veo31-fast"],
    displayName: "Veo 3.1 Fast",
    provider: "Google",
    type: "video",
    tier: "standard",
    version: 3.1,
  },

  // Seedance (ByteDance)
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_1_LITE,
    slug: "seedance-1-lite",
    aliases: ["seedance-lite"],
    displayName: "Seedance 1 Lite",
    provider: "ByteDance",
    type: "video",
    tier: "other",
    version: 1.0,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_1_5_PRO,
    slug: "seedance-1-5-pro",
    aliases: ["seedance-pro-1-5"],
    displayName: "Seedance 1.5 Pro",
    provider: "ByteDance",
    type: "video",
    tier: "pro",
    version: 1.5,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_1_PRO,
    slug: "seedance-1-pro",
    aliases: ["seedance-pro"],
    displayName: "Seedance 1 Pro",
    provider: "ByteDance",
    type: "video",
    tier: "pro",
    version: 1.0,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_1_PRO_FAST,
    slug: "seedance-1-pro-fast",
    aliases: ["seedance-pro-fast"],
    displayName: "Seedance 1 Pro Fast",
    provider: "ByteDance",
    type: "video",
    tier: "standard",
    version: 1.0,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_2,
    slug: "seedance-2",
    aliases: ["seedance-2-0"],
    displayName: "Seedance 2.0",
    provider: "ByteDance",
    type: "video",
    tier: "pro",
    version: 2.0,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_2_FAST,
    slug: "seedance-2-fast",
    aliases: ["seedance-2-0-fast"],
    displayName: "Seedance 2.0 Fast",
    provider: "ByteDance",
    type: "video",
    tier: "standard",
    version: 2.0,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_2_MINI,
    slug: "seedance-2-mini",
    aliases: ["seedance-2-0-mini"],
    displayName: "Seedance 2.0 Mini",
    provider: "ByteDance",
    type: "video",
    tier: "other",
    version: 2.0,
  },
  {
    id: SeedanceVideoGenerationModelType.SEEDANCE_2_5,
    slug: "seedance-2-5",
    aliases: ["seedance-25"],
    displayName: "Seedance 2.5",
    provider: "ByteDance",
    type: "video",
    tier: "pro",
    version: 2.5,
  },

  // HappyHorse
  {
    id: HappyHorseVideoGenerationModelType.HAPPYHORSE_1_1,
    slug: "happyhorse-1-1",
    aliases: ["happy-horse-1-1"],
    displayName: "HappyHorse 1.1",
    provider: "Alibaba",
    type: "video",
    tier: "standard",
    version: 1.1,
  },

  // Wavespeed Wan
  {
    id: WavespeedVideoGenerationModelType.WAN_2_5_I2V,
    slug: "wan-2-5-i2v",
    aliases: ["wan25-i2v"],
    displayName: "Wan 2.5 I2V",
    provider: "Wavespeed",
    type: "video",
    tier: "standard",
    version: 2.5,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_5_T2V,
    slug: "wan-2-5-t2v",
    aliases: ["wan25-t2v"],
    displayName: "Wan 2.5 T2V",
    provider: "Wavespeed",
    type: "video",
    tier: "standard",
    version: 2.5,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_5_I2V_FAST,
    slug: "wan-2-5-i2v-fast",
    aliases: ["wan25-i2v-fast"],
    displayName: "Wan 2.5 I2V Fast",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.5,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_5_T2V_FAST,
    slug: "wan-2-5-t2v-fast",
    aliases: ["wan25-t2v-fast"],
    displayName: "Wan 2.5 T2V Fast",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.5,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_1_I2V_720P,
    slug: "wan-2-1-i2v-720p",
    aliases: ["wan21-i2v-720p"],
    displayName: "Wan 2.1 I2V 720p",
    provider: "Wavespeed",
    type: "video",
    tier: "standard",
    version: 2.1,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_1_I2V_480P,
    slug: "wan-2-1-i2v-480p",
    aliases: ["wan21-i2v-480p"],
    displayName: "Wan 2.1 I2V 480p",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.1,
  },
  // Wavespeed WAN 2.1 T2V (Replicate)
  {
    id: WavespeedVideoGenerationModelType.WAN_2_1_T2V_480P,
    slug: "wan-2-1-t2v-480p",
    aliases: ["wan21-t2v-480p"],
    displayName: "Wan 2.1 T2V 480p",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.1,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_1_T2V_720P_REPLICATE,
    slug: "wan-2-1-t2v-720p-replicate",
    aliases: ["wan21-t2v-720p-replicate"],
    displayName: "Wan 2.1 T2V 720p (Replicate)",
    provider: "Wavespeed",
    type: "video",
    tier: "standard",
    version: 2.1,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_2_5B_FAST,
    slug: "wan-2-2-5b-fast",
    aliases: ["wan22-5b-fast"],
    displayName: "Wan 2.2 5B Fast",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.2,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_2_I2V_A14B,
    slug: "wan-2-2-i2v-a14b",
    aliases: ["wan22-i2v-a14b"],
    displayName: "Wan 2.2 I2V A14B",
    provider: "Wavespeed",
    type: "video",
    tier: "standard",
    version: 2.2,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_1_T2V_720P,
    slug: "wan-2-1-t2v-720p",
    aliases: ["wan21-t2v-720p"],
    displayName: "Wan 2.1 T2V 720p",
    provider: "Wavespeed",
    type: "video",
    tier: "standard",
    version: 2.1,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_6_T2V,
    slug: "wan-2-6-t2v",
    aliases: ["wan26-t2v", "alibaba-wan-2-6-t2v"],
    displayName: "Wan 2.6 T2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 2.6,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_6_I2V,
    slug: "wan-2-6-i2v",
    aliases: ["wan26-i2v", "alibaba-wan-2-6-i2v"],
    displayName: "Wan 2.6 I2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 2.6,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_6_I2V_FLASH,
    slug: "wan-2-6-i2v-flash",
    aliases: ["wan26-i2v-flash", "alibaba-wan-2-6-i2v-flash"],
    displayName: "Wan 2.6 I2V Flash",
    provider: "Alibaba",
    type: "video",
    tier: "other",
    version: 2.6,
  },

  // Wan 2.7 models
  {
    id: WavespeedVideoGenerationModelType.WAN_2_7_T2V,
    slug: "wan-2-7-t2v",
    aliases: ["wan27-t2v", "wan-2-7-text-to-video"],
    displayName: "Wan 2.7 T2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 2.7,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_7_I2V,
    slug: "wan-2-7-i2v",
    aliases: ["wan27-i2v", "wan-2-7-image-to-video"],
    displayName: "Wan 2.7 I2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 2.7,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_3_0_T2V,
    slug: "wan-3-0-t2v",
    aliases: ["wan30-t2v", "wan-3-text-to-video"],
    displayName: "Wan 3.0 T2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 3.0,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_3_0_I2V,
    slug: "wan-3-0-i2v",
    aliases: ["wan30-i2v", "wan-3-image-to-video"],
    displayName: "Wan 3.0 I2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 3.0,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_3_0_PRIME_T2V,
    slug: "wan-3-0-prime-t2v",
    aliases: ["wan30-prime-t2v", "wan-3-prime-text-to-video"],
    displayName: "Wan 3.0 Prime T2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 3.0,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_3_0_PRIME_I2V,
    slug: "wan-3-0-prime-i2v",
    aliases: ["wan30-prime-i2v", "wan-3-prime-image-to-video"],
    displayName: "Wan 3.0 Prime I2V",
    provider: "Alibaba",
    type: "video",
    tier: "pro",
    version: 3.0,
  },

  // Kling 2.1 Master
  {
    id: KlingAIVideoGenerationModelType.KLING_2_1_MASTER,
    slug: "kling-2-1-master",
    aliases: ["kling21-master"],
    displayName: "Kling 2.1 Master",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 2.1,
  },

  // Kling 3.0 Omni
  {
    id: KlingAIVideoGenerationModelType.KLING_3_0_OMNI,
    slug: "kling-3-0-omni",
    aliases: ["kling30-omni", "kling-omni"],
    displayName: "Kling 3.0 Omni",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 3.0,
  },

  // Kling 3.0 Motion Control
  {
    id: KlingAIVideoGenerationModelType.KLING_3_0_MOTION_CONTROL,
    slug: "kling-3-0-motion-control",
    aliases: ["kling30-motion"],
    displayName: "Kling 3.0 Motion Control",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 3.0,
  },

  // Kling O3
  {
    id: KlingAIVideoGenerationModelType.KLING_O3,
    slug: "kling-o3",
    aliases: ["kling-omni-3"],
    displayName: "Kling O3",
    provider: "Kling",
    type: "video",
    tier: "pro",
    version: 3.0,
  },
  // Minimax Hailuo 02 (standard)
  {
    id: MinimaxVideoGenerationModelType.HAILUO_02,
    slug: "hailuo-02",
    aliases: ["minimax-hailuo-02", "hailuo02"],
    displayName: "Hailuo 02",
    provider: "MiniMax",
    type: "video",
    tier: "standard",
    version: 2.0,
  },
  // Wan 2.2 fast variants
  {
    id: WavespeedVideoGenerationModelType.WAN_2_2_I2V_FAST,
    slug: "wan-2-2-i2v-fast",
    aliases: ["wan22-i2v-fast"],
    displayName: "Wan 2.2 I2V Fast",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.2,
  },
  {
    id: WavespeedVideoGenerationModelType.WAN_2_2_T2V_FAST,
    slug: "wan-2-2-t2v-fast",
    aliases: ["wan22-t2v-fast"],
    displayName: "Wan 2.2 T2V Fast",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 2.2,
  },
  // SkyReels V4 (WaveSpeed) — flat $0.02/sec across all three modes
  {
    id: SkyReelsVideoGenerationModelType.SKYREELS_V4_T2V,
    slug: "skyreels-v4-t2v",
    aliases: ["skyreels-v4-text-to-video", "skyreels-t2v"],
    displayName: "SkyReels V4 Text-to-Video",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 4.0,
  },
  {
    id: SkyReelsVideoGenerationModelType.SKYREELS_V4_I2V,
    slug: "skyreels-v4-i2v",
    aliases: ["skyreels-v4-image-to-video", "skyreels-i2v"],
    displayName: "SkyReels V4 Image-to-Video",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 4.0,
  },
  {
    id: SkyReelsVideoGenerationModelType.SKYREELS_V4_R2V,
    slug: "skyreels-v4-r2v",
    aliases: ["skyreels-v4-reference-to-video", "skyreels-r2v"],
    displayName: "SkyReels V4 Reference-to-Video",
    provider: "Wavespeed",
    type: "video",
    tier: "other",
    version: 4.0,
  },
  // HunyuanVideo Fast
  {
    id: HunyuanVideoGenerationModelType.HUNYUAN_VIDEO_FAST,
    slug: "hunyuan-video-fast",
    aliases: ["hunyuan-fast", "hunyuan"],
    displayName: "HunyuanVideo Fast",
    provider: "Tencent",
    type: "video",
    tier: "standard",
    version: 1.0,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // IMAGE MODELS
  // ═══════════════════════════════════════════════════════════════════════════

  // OpenAI
  {
    id: OpenAIImageGenerationModelType.GPT_IMAGE,
    slug: "gpt-image",
    aliases: ["gpt-image-1"],
    displayName: "GPT Image",
    provider: "OpenAI",
    type: "image",
    tier: "pro",
    version: 1.0,
  },
  {
    id: OpenAIImageGenerationModelType.GPT_IMAGE_1_5,
    slug: "gpt-image-1-5",
    aliases: ["gpt-image-15"],
    displayName: "GPT Image 1.5",
    provider: "OpenAI",
    type: "image",
    tier: "pro",
    version: 1.5,
  },
  {
    id: OpenAIImageGenerationModelType.GPT_IMAGE_1_MINI,
    slug: "gpt-image-mini",
    aliases: ["gpt-image-1-mini"],
    displayName: "GPT Image Mini",
    provider: "OpenAI",
    type: "image",
    tier: "other",
    version: 1.0,
  },
  {
    id: OpenAIImageGenerationModelType.DALLE_3,
    slug: "dalle-3",
    aliases: ["dall-e-3", "dalle3"],
    displayName: "DALL-E 3",
    provider: "OpenAI",
    type: "image",
    tier: "standard",
    version: 3.0,
  },

  // Flux (Black Forest Labs)
  {
    id: FluxImageGenerationModelType.FLUX_PRO,
    slug: "flux-pro",
    aliases: ["flux-1-pro"],
    displayName: "Flux Pro",
    provider: "Black Forest Labs",
    type: "image",
    tier: "pro",
    version: 1.0,
  },
  {
    id: FluxImageGenerationModelType.FLUX_DEV,
    slug: "flux-dev",
    aliases: ["flux-1-dev"],
    displayName: "Flux Dev",
    provider: "Black Forest Labs",
    type: "image",
    tier: "standard",
    version: 1.0,
  },
  {
    id: FluxImageGenerationModelType.FLUX_SCHNELL,
    slug: "flux-schnell",
    aliases: ["flux-fast"],
    displayName: "Flux Schnell",
    provider: "Black Forest Labs",
    type: "image",
    tier: "other",
    version: 1.0,
  },

  // Google Imagen
  {
    id: GoogleImageGenerationModelType.IMAGEN_4_ULTRA,
    slug: "imagen-4-ultra",
    aliases: ["imagen4-ultra"],
    displayName: "Imagen 4 Ultra",
    provider: "Google",
    type: "image",
    tier: "pro",
    version: 4.0,
  },
  {
    id: GoogleImageGenerationModelType.IMAGEN_4,
    slug: "imagen-4",
    aliases: ["imagen4"],
    displayName: "Imagen 4",
    provider: "Google",
    type: "image",
    tier: "standard",
    version: 4.0,
  },
  {
    id: GoogleImageGenerationModelType.IMAGEN_4_FAST,
    slug: "imagen-4-fast",
    aliases: ["imagen4-fast"],
    displayName: "Imagen 4 Fast",
    provider: "Google",
    type: "image",
    tier: "other",
    version: 4.0,
  },
  {
    id: GoogleImageGenerationModelType.NANO_BANANA_PRO,
    slug: "nano-banana-pro",
    aliases: ["nanobanana-pro"],
    displayName: "Nano Banana Pro",
    provider: "Google",
    type: "image",
    tier: "pro",
    version: 1.0,
  },
  {
    id: GoogleImageGenerationModelType.NANO_BANANA,
    slug: "nano-banana",
    aliases: ["nanobanana"],
    displayName: "Nano Banana",
    provider: "Google",
    type: "image",
    tier: "standard",
    version: 1.0,
  },
  {
    id: GoogleImageGenerationModelType.GEMINI_2_5_FLASH_IMAGE,
    slug: "gemini-2-5-flash-image",
    aliases: ["gemini-flash-image"],
    displayName: "Gemini 2.5 Flash Image",
    provider: "Google",
    type: "image",
    tier: "standard",
    version: 2.5,
  },

  // Nvidia
  {
    id: NvidiaImageGenerationModelType.SANA,
    slug: "nvidia-sana",
    aliases: ["sana"],
    displayName: "NVIDIA Sana",
    provider: "NVIDIA",
    type: "image",
    tier: "standard",
    version: 1.0,
  },

  // MiniMax Image
  {
    id: MinimaxImageGenerationModelType.MINIMAX_IMAGE_01,
    slug: "minimax-image",
    aliases: ["minimax-image-01"],
    displayName: "MiniMax Image",
    provider: "MiniMax",
    type: "image",
    tier: "standard",
    version: 1.0,
  },

  // ByteDance Seedream
  {
    id: EByteDanceImageGenerationModelType.SEEDREAM_4_5,
    slug: "seedream-4-5",
    aliases: ["seedream-45"],
    displayName: "Seedream 4.5",
    provider: "ByteDance",
    type: "image",
    tier: "pro",
    version: 4.5,
  },
  {
    id: EByteDanceImageGenerationModelType.SEEDREAM_4,
    slug: "seedream-4",
    aliases: ["seedream4"],
    displayName: "Seedream 4",
    provider: "ByteDance",
    type: "image",
    tier: "standard",
    version: 4.0,
  },
]

// ─────────────────────────────────────────────────────────────────────────────
// BUILD LOOKUP MAPS
// ─────────────────────────────────────────────────────────────────────────────

// Map from model ID to ModelInfo
const ID_TO_MODEL_MAP = new Map<string, ModelInfo>()

// Map from any slug (canonical or alias) to ModelInfo
const SLUG_TO_MODEL_MAP = new Map<string, ModelInfo>()

// Initialize maps
for (const model of MODEL_REGISTRY) {
  ID_TO_MODEL_MAP.set(model.id, model)
  SLUG_TO_MODEL_MAP.set(model.slug, model)
  for (const alias of model.aliases) {
    SLUG_TO_MODEL_MAP.set(alias, model)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert model ID to canonical URL slug
 * @example modelIdToSlug("kwaivgi/kling-v2.6-standard") → "kling-standard-2-6"
 */
export function modelIdToSlug(modelId: string): string | null {
  const model = ID_TO_MODEL_MAP.get(modelId)
  return model?.slug ?? null
}

/**
 * Get all slugs for a model (canonical + aliases)
 * @example modelIdToSlugs("kwaivgi/kling-v2.6-standard") → ["kling-standard-2-6", "kling-2-6"]
 */
export function modelIdToSlugs(modelId: string): string[] {
  const model = ID_TO_MODEL_MAP.get(modelId)
  if (!model) return []
  return [model.slug, ...model.aliases]
}

/**
 * Convert any slug (canonical or alias) back to model ID
 * @example slugToModelId("kling-2-6") → "kwaivgi/kling-v2.6-standard"
 */
export function slugToModelId(slug: string): string | null {
  const model = SLUG_TO_MODEL_MAP.get(slug)
  return model?.id ?? null
}

/**
 * Get model info from slug
 */
export function getModelInfoFromSlug(slug: string): ModelInfo | null {
  return SLUG_TO_MODEL_MAP.get(slug) ?? null
}

/**
 * Get model info from model ID
 */
export function getModelInfo(modelId: string): ModelInfo | null {
  return ID_TO_MODEL_MAP.get(modelId) ?? null
}

/**
 * Determine if model is video or image
 */
export function getModelType(modelId: string): "video" | "image" | null {
  const model = ID_TO_MODEL_MAP.get(modelId)
  return model?.type ?? null
}

/**
 * Check if a slug is valid (maps to a known model)
 */
export function isValidModelSlug(slug: string): boolean {
  return SLUG_TO_MODEL_MAP.has(slug)
}

// ─────────────────────────────────────────────────────────────────────────────
// SORTING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sort priority calculation (lower = higher priority)
 * - Pro models first (-1000)
 * - Higher versions first (-version * 100)
 */
function getSortPriority(model: ModelInfo): number {
  let priority = 0

  // Tier: Pro models first
  if (model.tier === "pro") priority -= 1000
  if (model.tier === "other") priority += 500 // Push "other" tier to the end

  // Version: Higher versions first
  priority -= model.version * 100

  return priority
}

/**
 * Get sorted video models (Pro first, higher versions first)
 */
export function getSortedVideoModels(): ModelInfo[] {
  return MODEL_REGISTRY.filter((m) => m.type === "video").sort((a, b) => getSortPriority(a) - getSortPriority(b))
}

/**
 * Get sorted image models (Pro first, higher versions first)
 */
export function getSortedImageModels(): ModelInfo[] {
  return MODEL_REGISTRY.filter((m) => m.type === "image").sort((a, b) => getSortPriority(a) - getSortPriority(b))
}

/**
 * Get all video model IDs (for validation)
 */
export function getAllVideoModelIds(): string[] {
  return MODEL_REGISTRY.filter((m) => m.type === "video").map((m) => m.id)
}

/**
 * Get all image model IDs (for validation)
 */
export function getAllImageModelIds(): string[] {
  return MODEL_REGISTRY.filter((m) => m.type === "image").map((m) => m.id)
}

/**
 * Get all valid slugs (canonical + aliases) for URL validation
 */
export function getAllValidSlugs(): string[] {
  return Array.from(SLUG_TO_MODEL_MAP.keys())
}
